import { useEffect, useState } from "react";
import { api, errDetail } from "../lib/api";
import { useApp } from "../lib/store";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { ArticleFormFields, useAccountsList } from "./ArticleForm";

const COND_TYPES = ["price", "discount", "payment_terms", "freight"];

/** Inline edit dialog for a purchasing/sales condition — reusable on the account page. */
export function ConditionEditDialog({ condition, onClose, onSaved }) {
  const { t } = useApp();
  const [editing, setEditing] = useState(condition);
  const [accounts, setAccounts] = useState([]);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    api.get("/accounts?limit=500").then((r) => setAccounts(r.data.sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {});
  }, []);
  useEffect(() => {
    if (editing?.account_id) api.get(`/articles?account_id=${editing.account_id}&page_size=200`).then((r) => setArticles(r.data.items)).catch(() => {});
    else setArticles([]);
  }, [editing?.account_id]);

  const set = (k, v) => setEditing((e) => ({ ...e, [k]: v }));
  const typeLabel = (ty) => t(`cond_type_${ty === "payment_terms" ? "payment" : ty}`);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/conditions/${editing.id}`, editing);
      else await api.post("/conditions", editing);
      toast.success(t("save") + " ✓");
      onSaved();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const remove = async () => {
    try { await api.delete(`/conditions/${editing.id}`); toast.success(t("delete") + " ✓"); onSaved(); }
    catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="account-condition-edit-dialog">
        <DialogHeader>
          <DialogTitle>{editing?.id ? t("edit") : t("cond_new")}</DialogTitle>
          <DialogDescription>{t("order_conditions")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{t("ord_customer")} *</Label>
              <select data-testid="condition-account-select" className="w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm mt-1"
                value={editing.account_id || ""} onChange={(e) => set("account_id", e.target.value)}>
                <option value="">–</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">{t("cond_type")}</Label>
              <select data-testid="condition-type-select" className="w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm mt-1"
                value={editing.condition_type} onChange={(e) => set("condition_type", e.target.value)}>
                {COND_TYPES.map((ty) => <option key={ty} value={ty}>{typeLabel(ty)}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">{t("cond_article")}</Label>
              <select data-testid="condition-article-select" className="w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm mt-1"
                value={editing.article_id || ""} onChange={(e) => set("article_id", e.target.value)}>
                <option value="">{t("cond_all_articles")}</option>
                {articles.map((a) => <option key={a.id} value={a.id}>{`${a.article_number} – ${a.name}`}</option>)}
              </select>
            </div>
            {editing.condition_type === "price" && (
              <div>
                <Label className="text-xs">{t("cond_price")} (€)</Label>
                <Input type="number" step="0.01" data-testid="condition-price-input" className="mt-1" value={editing.price || ""} onChange={(e) => set("price", e.target.value)} />
              </div>
            )}
            {editing.condition_type === "discount" && (
              <div>
                <Label className="text-xs">{t("cond_discount")}</Label>
                <Input type="number" step="0.5" className="mt-1" value={editing.discount_pct || ""} onChange={(e) => set("discount_pct", e.target.value)} />
              </div>
            )}
            {editing.condition_type === "payment_terms" && (
              <div>
                <Label className="text-xs">{t("cond_payment")}</Label>
                <Input className="mt-1" placeholder="z. B. Net 45" value={editing.payment_terms || ""} onChange={(e) => set("payment_terms", e.target.value)} />
              </div>
            )}
            <div>
              <Label className="text-xs">{t("cond_min_qty")}</Label>
              <Input type="number" className="mt-1" value={editing.min_qty || ""} onChange={(e) => set("min_qty", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">{t("cond_valid_from")}</Label>
              <Input type="date" className="mt-1" value={editing.valid_from || ""} onChange={(e) => set("valid_from", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">{t("cond_valid_to")}</Label>
              <Input type="date" className="mt-1" value={editing.valid_to || ""} onChange={(e) => set("valid_to", e.target.value)} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">{t("notes")}</Label>
              <Input className="mt-1" value={editing.notes || ""} onChange={(e) => set("notes", e.target.value)} />
            </div>
          </div>
          <div className="flex justify-between pt-2">
            {editing.id ? (
              <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={remove} data-testid="condition-delete-btn">
                <Trash2 className="h-3.5 w-3.5 mr-1" />{t("delete")}
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
              <Button className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold" onClick={save} data-testid="condition-save-btn">{t("save")}</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Inline edit dialog for an article master record — reusable on the account page. */
export function ArticleEditDialog({ article, onClose, onSaved }) {
  const { t } = useApp();
  const [editing, setEditing] = useState(article);
  const accounts = useAccountsList();

  const set = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  const save = async () => {
    try {
      if (editing.id) await api.put(`/articles/${editing.id}`, editing);
      else await api.post("/articles", editing);
      toast.success(t("save") + " ✓");
      onSaved();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const remove = async () => {
    try {
      const r = await api.delete(`/articles/${editing.id}`);
      toast.success(r.data.deactivated ? t("art_deactivated") : t("delete") + " ✓");
      onSaved();
    } catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="account-article-edit-dialog">
        <DialogHeader>
          <DialogTitle>{editing?.id ? `${t("edit")} – ${editing.article_number}` : t("art_new")}</DialogTitle>
          <DialogDescription>{t("article_master")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <ArticleFormFields editing={editing} set={set} accounts={accounts} />
          <div className="flex justify-between pt-2">
            {editing.id ? (
              <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={remove} data-testid="article-delete-btn">
                <Trash2 className="h-3.5 w-3.5 mr-1" />{t("delete")}
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
              <Button className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold" onClick={save} data-testid="article-save-btn">{t("save")}</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
